import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { AutogenLayoutProspectActionList } from "@mcleod/crm/src/autogen/AutogenLayoutProspectActionList";
import { ModelRow, SortDirection, SortUtil, StringUtil } from "@mcleod/core";
import { ClickEvent, ContextMenuCreationEvent, DataSourceMode, DataSourceModeChangeEvent, Layout, LookupModelSelectionEvent, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { EditRowDecoratorProps } from "@mcleod/components/src/page/decorators/EditRowDecoratorProps";
import { ProspectAction } from "./ProspectAction";
import { ModelProspect, RowProspect } from "./models/ModelProspect";

export class ProspectActionList extends AutogenLayoutProspectActionList {
    private _prospectId: string;
    private _opportunityId: number;

    private actionFilterPanel: FilterPanel;
    private actionSecondaryFilters: SecondaryFilterProps[] = [{
        id: "upcomingActions",
        caption: "Upcoming",
        filter: (row: ModelRow) => row.isNull('action_date')
    },{
        id: "completedActions",
        caption: "Completed",
        filter: (row: ModelRow) => !row.isNull('action_date')
    }];

    onLoad(): void | Promise<void> {
        this.actionFilterPanel = new FilterPanel(this.actionSecondaryFilters, this.tableActivities, this.panelSecondaryFilters);
        this.tableActivities.onEditLayoutLoaded = (decorator, tableRow) => decorator.multiButton.dropdownItems = null;
    }

    sourceProspectActionAfterModeChange(event: DataSourceModeChangeEvent) {
        this.actionFilterPanel.panel.visible = event.newMode !== DataSourceMode.SEARCH;
    }

    buttonAddActionOnClick(event: ClickEvent) {
        ProspectAction.showAddActionSlideout(null, {
            doAfterSlideIn: (slideout) => {
                const layout = slideout.layout as ProspectAction;
                layout.textboxCustomerId.enabled = false;
                new ModelProspect().searchSingle({ "id": this._prospectId}, "lme/crm/Prospect")
                .then((customer: RowProspect) => {
                    if (!customer) {
                        return;
                    }
                    layout.activeRow.setValueAndLookupModelData("customer_id", this._prospectId, {name: customer.getString("name")});
                    layout.textboxCustomerIdOnLookupModelSelection(new LookupModelSelectionEvent(layout.textboxCustomerId, customer));
                });
                if (!StringUtil.isEmptyString(this.opportunityId)) {
                    layout.activeRow.setValueAndLookupModelData("opportunity_id", this.opportunityId.toString(), {
                            id: this.opportunityId.toString(),
                    });
                    layout.textboxOpportunityId.displayComponentData(layout.activeRow, null, 0);
                }
            },
            onSave: (updatedData) =>
                {
                    if (this.mainDataSource.mode === DataSourceMode.ADD) {
                        this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [...this.mainDataSource.data, updatedData]);
                    } else {
                        updatedData.post().then(() => this.mainDataSource.search({ customer_id: this._prospectId }));
                    }
                }
        });
    }

    tableActivitiesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const status = modelRow.getString("status", null);
        tableRow.allowDelete = "C" !== status;

        const textboxObjectionId1 = tableRow.findComponentById("textboxObjectionId1") as Textbox;
        if (textboxObjectionId1) {
            textboxObjectionId1.quickInfoOverrideSearchMethod = async (layout: Layout, _keyValue: any) => 
                layout.mainDataSource.search({id: _keyValue, type_of: "C"});
        }
        const textboxObjectionId2 = tableRow.findComponentById("textboxObjectionId2") as Textbox;
        if (textboxObjectionId2) {
            textboxObjectionId2.quickInfoOverrideSearchMethod = async (layout: Layout, _keyValue: any) => 
                layout.mainDataSource.search({id: _keyValue, type_of: "C"});
        }
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const activityId = modelRow.getString("activity_id");
        const dateDue = modelRow.getDate("date_due");
        let props: Partial<EditRowDecoratorProps> = {
            onSave: (updatedData) => {
                if (this.mainDataSource.mode === DataSourceMode.ADD) {
                    this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [...this.mainDataSource.data, updatedData]);
                }
                else {
                    updatedData.post().then(() => this.mainDataSource.search({ customer_id: this.prospectId }))
                }
            }
        };
        if ("C" !== modelRow.getString("status", null)) {
            props = {
                ...props,
                onDelete: () => this.mainDataSource.search({ customer_id: this.properName })
            }
        }

        ProspectAction.showEditActionSlideout(modelRow, props);
    }

    findNextActivity(activityId: string, dateDue: Date): {id: string, descr: string, date_due: Date} {
        const carrierActivities = this.mainDataSource.data.filter((row: ModelRow) => row.isNull('action_date'))
            .sort((a:ModelRow, b:ModelRow) => {
                const sortInfo:{fieldName: string, sort: SortDirection}[] = [
                    {fieldName: "date_due", sort: "asc"},
                    {fieldName: "priority", sort: "desc"}
                ]
                for (const fieldSortInfo of sortInfo) {
                    const valueA = SortUtil.getSortTestValue(a, fieldSortInfo.fieldName);
                    const valueB = SortUtil.getSortTestValue(b, fieldSortInfo.fieldName);
                    const compareResult = SortUtil.compareTwoValues(valueA, valueB, fieldSortInfo.sort, false);
                    if (compareResult !== 0)
                        return compareResult;
                }
                return 0;
            });
        let nextActivity = null;
        if (!activityId && !dateDue) {
            nextActivity = carrierActivities[0];
        } else {
            let i = 0;
            let rowMatch = false;
            while(!rowMatch && i < carrierActivities.length) {
                const activityRow = carrierActivities[i++];
                if (activityId === activityRow.getString("activity_id")
                && dateDue.getTime() === activityRow.getDate("date_due").getTime()) {
                    rowMatch = true;
                }
            }
            nextActivity = carrierActivities[i];
        }
        return {
            id: nextActivity?.getString("activity_id"),
            descr: nextActivity?.getString("descr"),
            date_due: nextActivity?.getDate("date_due")
        };
    }

    public get prospectId(): string {
        return this._prospectId;
    }
    public set prospectId(value: string) {
        this._prospectId = value;
    }
    public get opportunityId(): number {
        return this._opportunityId;
    }
    public set opportunityId(value: number) {
        this._opportunityId = value;
    }

}
